/**
 * Driver My Vehicles Page
 * صفحة مركبات السائق
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useDriverAuth } from '../../context/DriverAuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';
const call = async (method, path, body, token) => {
  const r = await fetch(`${API}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || 'خطأ في الاتصال');
  return d;
};

const FUEL_TYPES = {
  diesel:  { label: 'ديزل',     color: 'text-gray-600' },
  petrol:  { label: 'بنزين',    color: 'text-blue-600' },
  hybrid:  { label: 'هجين',     color: 'text-green-600' },
  electric:{ label: 'كهربائي', color: 'text-purple-600' },
};

const emptyForm = {
  make: '', model: '', year: '', plate_number: '', plate_letters: '',
  fuel_type: 'diesel', capacity_tons: '', notes: '',
};

export default function VehiclesPage() {
  const { token } = useDriverAuth();

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'add' | vehicle_obj
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await call('GET', '/driver/vehicles', null, token);
      setVehicles(d || []);
    } catch { showToast('فشل تحميل المركبات', false); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm(emptyForm);
    setModal('add');
  };

  const openEdit = (v) => {
    setForm({
      make: v.make || '',
      model: v.model || '',
      year: v.year || '',
      plate_number: v.plate_number || '',
      plate_letters: v.plate_letters || '',
      fuel_type: v.fuel_type || 'diesel',
      capacity_tons: v.capacity_tons || '',
      notes: v.notes || '',
    });
    setModal(v);
  };

  const saveVehicle = async () => {
    if (!form.make.trim() || !form.model.trim()) {
      showToast('اسم الشركة المصنعة والطراز مطلوبان', false); return;
    }
    setSaving(true);
    try {
      const body = {
        make: form.make.trim(),
        model: form.model.trim(),
        year: form.year ? parseInt(form.year) : null,
        plate_number: form.plate_number.trim() || null,
        plate_letters: form.plate_letters.trim() || null,
        fuel_type: form.fuel_type,
        capacity_tons: form.capacity_tons ? parseFloat(form.capacity_tons) : null,
        notes: form.notes.trim() || null,
      };
      if (modal === 'add') {
        await call('POST', '/driver/vehicles', body, token);
        showToast('تمت إضافة المركبة');
      } else {
        await call('PUT', `/driver/vehicles/${modal.id}`, body, token);
        showToast('تم تحديث المركبة');
      }
      setModal(null);
      load();
    } catch (e) { showToast(e.message, false); }
    finally { setSaving(false); }
  };

  const deleteVehicle = async (id) => {
    try {
      await call('DELETE', `/driver/vehicles/${id}`, null, token);
      showToast('تم حذف المركبة');
      setConfirmDel(null);
      load();
    } catch (e) { showToast(e.message, false); }
  };

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="min-h-screen bg-gray-50 pb-24" dir="rtl">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 rounded-xl shadow-lg text-sm font-bold
          ${toast.ok ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 pt-safe pb-3 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-black text-gray-900">مركباتي</h1>
            <p className="text-xs text-gray-500">أضف وأدر مركباتك الخاصة</p>
          </div>
          <button onClick={openAdd}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-500 text-white text-sm font-bold shadow-sm active:scale-95 transition-all">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            إضافة
          </button>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-7 h-7 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"/>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-16 text-gray-400 space-y-2">
            <p className="text-4xl">🚛</p>
            <p className="text-sm font-bold text-gray-500">لا توجد مركبات بعد</p>
            <p className="text-xs">اضغط "إضافة" لتسجيل مركبتك</p>
            <button onClick={openAdd}
              className="mt-3 px-6 py-2.5 rounded-xl bg-blue-500 text-white text-sm font-bold shadow-sm">
              إضافة مركبة
            </button>
          </div>
        ) : vehicles.map(v => {
          const fuel = FUEL_TYPES[v.fuel_type] || { label: v.fuel_type, color: 'text-gray-600' };
          return (
            <div key={v.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-start justify-between gap-3">
                {/* Icon + info */}
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-2xl flex-shrink-0">
                    🚛
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-gray-900">{v.make} {v.model}</p>
                    {v.year && <p className="text-xs text-gray-400">سنة {v.year}</p>}
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {v.plate_number && (
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-lg font-mono font-bold border border-gray-200">
                          {v.plate_letters && `${v.plate_letters} `}{v.plate_number}
                        </span>
                      )}
                      <span className={`text-xs font-bold ${fuel.color}`}>{fuel.label}</span>
                      {v.capacity_tons && (
                        <span className="text-xs text-blue-600 font-bold">{v.capacity_tons} طن</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(v)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-50 text-gray-400 hover:text-blue-500 transition-colors">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                    </svg>
                  </button>
                  <button onClick={() => setConfirmDel(v.id)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
              {v.notes && <p className="text-xs text-gray-400 mt-2 border-t border-gray-50 pt-2">{v.notes}</p>}
            </div>
          );
        })}
      </div>

      {/* Add/Edit Modal (bottom sheet) */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-end" dir="rtl">
          <div className="absolute inset-0 bg-black/40" onClick={() => setModal(null)}/>
          <div className="relative w-full bg-white rounded-t-3xl shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-5 py-4 border-b border-gray-100">
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-3"/>
              <h2 className="text-lg font-black text-gray-900">
                {modal === 'add' ? 'إضافة مركبة جديدة' : 'تعديل المركبة'}
              </h2>
            </div>

            <div className="p-5 space-y-4">
              {/* Row: make + model */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1.5">الشركة المصنعة *</label>
                  <input value={form.make} onChange={e => setF('make', e.target.value)}
                    placeholder="مثال: تويوتا، هينو، إيسوزو"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"/>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1.5">الطراز *</label>
                  <input value={form.model} onChange={e => setF('model', e.target.value)}
                    placeholder="مثال: 500، هاي لوكس"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"/>
                </div>
              </div>

              {/* Row: year + capacity */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1.5">سنة الصنع</label>
                  <input value={form.year} onChange={e => setF('year', e.target.value)}
                    type="number" placeholder="2020" min="1990" max="2030"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"/>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1.5">السعة (طن)</label>
                  <input value={form.capacity_tons} onChange={e => setF('capacity_tons', e.target.value)}
                    type="number" step="0.5" placeholder="5"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"/>
                </div>
              </div>

              {/* Plate */}
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1.5">رقم اللوحة</label>
                <div className="flex gap-2">
                  <input value={form.plate_letters} onChange={e => setF('plate_letters', e.target.value)}
                    placeholder="أ ب ج" dir="ltr"
                    className="w-24 px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-center font-mono focus:outline-none focus:ring-2 focus:ring-blue-300"/>
                  <input value={form.plate_number} onChange={e => setF('plate_number', e.target.value)}
                    placeholder="1234" dir="ltr"
                    className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-center font-mono focus:outline-none focus:ring-2 focus:ring-blue-300"/>
                </div>
              </div>

              {/* Fuel type */}
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1.5">نوع الوقود</label>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(FUEL_TYPES).map(([key, ft]) => (
                    <button key={key} onClick={() => setF('fuel_type', key)}
                      className={`px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all
                        ${form.fuel_type === key
                          ? 'border-blue-400 bg-blue-50 text-blue-700'
                          : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'}`}>
                      {ft.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1.5">ملاحظات</label>
                <textarea value={form.notes} onChange={e => setF('notes', e.target.value)}
                  rows={2} placeholder="أي معلومات إضافية..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"/>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pb-safe">
                <button onClick={saveVehicle} disabled={saving}
                  className={`flex-1 py-3.5 rounded-xl text-white font-black text-sm transition-all
                    ${saving ? 'bg-gray-300' : 'bg-blue-500 hover:bg-blue-600 active:scale-[0.98] shadow-md'}`}>
                  {saving
                    ? <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"/>
                    : modal === 'add' ? 'إضافة المركبة' : 'حفظ التعديلات'}
                </button>
                <button onClick={() => setModal(null)}
                  className="px-5 py-3.5 rounded-xl bg-gray-100 text-gray-600 font-bold text-sm">
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {confirmDel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" dir="rtl">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmDel(null)}/>
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-xs text-center shadow-2xl">
            <p className="text-4xl mb-3">🗑️</p>
            <h3 className="font-black text-gray-900 mb-2">حذف المركبة؟</h3>
            <p className="text-sm text-gray-500 mb-5">لا يمكن التراجع عن هذا الإجراء</p>
            <div className="flex gap-2">
              <button onClick={() => deleteVehicle(confirmDel)}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-bold text-sm hover:bg-red-600">
                حذف
              </button>
              <button onClick={() => setConfirmDel(null)}
                className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-600 font-bold text-sm">
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
